import React from 'react';
import { Avatar, Box, Typography, Grid, Paper } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { login } from '../../services/accountsService';
import { collectClientSignals } from '../../shared/Utility/collectClientSignals';
import LoginForm from './LoginForm';
import LogoOrIcon from '../../shared/Utility/LogoOrIcon';
import { useTranslation } from 'react-i18next';
import { fetchPublicTenantInfo, getTenantBackgroundUrl, getTenantLogoUrl } from '../../services/tenantService';

import useGatewayJwt from './useGatewayJwt';
import LoginSkeleton from './LoginSkeleton';

const DEFAULT_LOGO = process.env.PUBLIC_URL + '/assets/logo.png';
const DEFAULT_BG = process.env.PUBLIC_URL + '/assets/bg_security.jpg';

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();

  const slug = (searchParams.get('s') || '').trim() || null;

  // read, toast, and strip jwt via the hook.
  const gatewayJwt = useGatewayJwt();

  // default assets by default
  const [logoUrl, setLogoUrl] = React.useState(DEFAULT_LOGO);
  const [bgUrl, setBgUrl] = React.useState(DEFAULT_BG);
  const [isTenantLoading, setIsTenantLoading] = React.useState(!!slug);

  const [clientSignals, setClientSignals] = React.useState(null);
  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const sig = await collectClientSignals();
        if (mounted) setClientSignals(sig);
      } catch {
        if (mounted) setClientSignals(null);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const preload = React.useCallback((url) => {
    if (!url) return Promise.resolve(null);
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(url);
      img.onerror = () => reject(new Error('Image failed to load: ' + url));
      img.src = url;
    });
  }, []);

  React.useEffect(() => {
    let mounted = true;

    // no tenant slug => just show defaults immediately
    if (!slug) {
      setLogoUrl(DEFAULT_LOGO);
      setBgUrl(DEFAULT_BG);
      setIsTenantLoading(false);
      return () => { mounted = false; };
    }

    (async () => {
      try {
        const info = await fetchPublicTenantInfo(slug);
        if (!mounted) return;

        // try tenant images, fallback to defaults if not present / 404
        const tenantLogoUrl = info ? getTenantLogoUrl(info.id) : null;
        const tenantBgUrl = info ? getTenantBackgroundUrl(info.id) : null;

        const [readyLogo, readyBg] = await Promise.all([
          preload(tenantLogoUrl).catch(() => null),
          preload(tenantBgUrl).catch(() => null),
        ]);
        if (!mounted) return;

        setLogoUrl(readyLogo || DEFAULT_LOGO);
        setBgUrl(readyBg || DEFAULT_BG);
      } catch (e) {
        console.error('Failed to load public tenant info:', e);
        if (!mounted) return;
        setLogoUrl(DEFAULT_LOGO);
        setBgUrl(DEFAULT_BG);
      } finally {
        if (mounted) setIsTenantLoading(false);
      }
    })();

    return () => { mounted = false; };
  }, [slug, preload]);

  const [needCode, setNeedCode] = React.useState(false);

  const handleLogin = async (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const username = String(data.get('username') || '');
    const password = String(data.get('password') || '');
    const code = String(data.get('code') || '');

    try {
      const signals = clientSignals ?? (await collectClientSignals().catch(() => null));
      const response = await login(username, password, gatewayJwt, signals, code);
      if (response.success) {
        navigate('/about-app?menu=1');
        return;
      }
      if (response.error === '2FA_REQUIRED') {
        setNeedCode(true);
        enqueueSnackbar(
          t('login_page.twofa_required', 'Zadajte overovací kód (2FA) z vašej autentifikačnej aplikácie.'),
          { variant: 'info' }
        );
        return;
      }
      if (response.error === '2FA_INVALID') {
        setNeedCode(true);
        enqueueSnackbar(
          t('login_page.twofa_invalid', 'Nesprávny alebo expirovaný 2FA kód.'),
          { variant: 'error' }
        );
        return;
      }
      enqueueSnackbar(
        t('login_page.login_failed', { error: response.error }),
        { variant: 'error' }
      );
    } catch (error) {
      enqueueSnackbar(
        t('login_page.login_failed', { error: (error && error.message) || 'Unknown error' }),
        { variant: 'error' }
      );
    }
  };

  return (
    <Grid container component="main" sx={{ minHeight: '100vh' }}>
      {/* LEFT: skeleton while loading; otherwise always show bg (default or tenant) */}
      {isTenantLoading ? (
        <Grid
          item
          xs={false}
          md={8}
          sx={{
            display: { xs: 'none', md: 'block' },
            position: 'relative',
          }}
        >
          <LoginSkeleton />
        </Grid>
      ) : (
        <Grid
          item
          xs={false}
          md={8}
          sx={{
            display: { xs: 'none', md: 'block' },
            backgroundImage: `url(${bgUrl})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      )}

      {/* RIGHT: Login panel */}
      <Grid
        item
        xs={12}
        md={4}
        component={Paper}
        elevation={0}
        square
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 3, sm: 6 },
        }}
      >
        {isTenantLoading ? (
          <LoginSkeleton />
        ) : (
          <Box sx={{ width: '100%', maxWidth: 420 }}>
            <LogoOrIcon
              open
              src={logoUrl}
              alt="Tenant Logo"
              sizeOpen={180}
              sx={{ mb: 4 }}
            />

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Avatar sx={{ bgcolor: 'primary.main', mb: 1 }}>
                <LockOutlinedIcon />
              </Avatar>

              <Typography component="h1" variant="h5" sx={{ mb: 1 }}>
                {t('login_page.sign_in')}
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                {t('login_page.subtitle', 'Continue to access your account')}
              </Typography>

              <LoginForm handleLogin={handleLogin} needCode={needCode} />
            </Box>
          </Box>
        )}
      </Grid>
    </Grid>
  );
};

export default Login;
